import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackPageView,
  trackUserClick,
  trackButtonClick,
  trackFormSubmit,
  trackModuleUsage,
  trackFeatureUsage
} from 'shared/observability/prometheus';

/**
 * Custom hook for tracking page views and user interactions
 *
 * Automatically tracks:
 * - Page views on route changes
 * - Module usage when specified
 *
 * Note: Button clicks and form submissions are tracked automatically via global
 * event listeners set up in App.tsx. No need to manually track common interactions.
 * @param pageTitle - Optional page title for tracking
 * @param moduleName - Optional module name for tracking
 * @example
 * ```tsx
 * // Basic usage - tracks page views and module usage
 * useMetrics('Dashboard', 'dashboard');
 *
 * // Track page view only
 * useMetrics('Settings');
 * ```
 * @returns Object with manual tracking functions for special cases
 */
export const useMetrics = (pageTitle?: string, moduleName?: string) => {
  const location = useLocation();

  useEffect(() => {
    // Track page view whenever location changes
    trackPageView(location.pathname, pageTitle);

    // Track module usage if specified
    if (moduleName) {
      trackModuleUsage(moduleName);
    }
  }, [location.pathname, pageTitle, moduleName]);

  // Helper functions for manual tracking (when needed for special cases)
  const trackClick = (elementType: string, elementId?: string, elementLabel?: string) => {
    trackUserClick(location.pathname, elementType, elementId, elementLabel);
  };

  const trackButton = (buttonId: string, buttonLabel?: string) => {
    trackButtonClick(location.pathname, buttonId, buttonLabel);
  };

  const trackForm = (formName: string) => {
    trackFormSubmit(location.pathname, formName);
  };

  const trackFeature = (featureName: string) => {
    trackFeatureUsage(featureName);
  };

  return {
    trackClick,
    trackButton,
    trackForm,
    trackFeature
  };
};
